import _ from 'lodash';
import { makeStyles } from '@mui/styles';
import { useParams } from 'react-router-dom';
import { useState, useEffect, useRef, useMemo } from 'react';
import dayjs from 'dayjs';

import useStyles from '../styling/jss';
import * as constants from '../utils/constants';
import { getAllMappings, getAllFunctionTypes } from '../utils/rosettaDataRequestUtils';
import { setMappingsData } from './useMappingsHook';
import { getAllCrosswalks } from '../utils/crosswalksDataRequestUtils';
import { setCrosswalks } from './useCrosswalksHook';
import { getAllComparisons } from '../utils/comparisonsDataRequestUtils';
import { setComparisons } from './useComparisonsHook';
import { useWatchUnsavedDataContext } from './useWatchUnsavedDataHook';
import { getOrganizationAppInstances } from '../utils/organizationUtils';
import { isNumber } from '../utils/helpers';
import {
    useSelectedIntegrationData,
    setSelectedIntegrationData,
    updateIntegrationsOptimistically,
} from './useIntegrationsHook';
import { showSnackBarErrorNotification, showSnackBarSuccessNotification } from '../utils/snackBarNotificationUtil';

import {
    useStoreState,
    useStoreValue,
    setStoreState,
    getStoreState,
    getStoreStates,
    setStoreStates,
    useStoreValues,
} from './useStoreStateHook';

import { getIntegrationById, testExecuteIntegration, updateIntegration } from '../utils/integrationDataRequestUtils';

import {
    getStepTypes,
    getTriggerList,
    getDefaultStepList,
    getEnabledStepTypes,
    getDefaultStepObject,
    getLastGeneratedStepId,
    getMutableIntegrationStepDeep,
    getMutableIntegrationStepByStepId,
    getConditionalStepRelatedNextStepsIds,
    getTranslatedStepDataFromIntegrationData,
    filterConditionalStepExpression,
    traverseStepsForValidation,
    normalizeAppRequestStepParameter,
    clearConditionalIndexValidationError,
    normalizeIntegrationStepsData,
} from '../utils/integrationUtils';
import { setMyDataTypeCreateDataTypeModalState } from './useModalStateHook';
import { useGetGlobalRefItem, useRemoveGlobalRefItem, useSetGlobalRefItem } from './useGlobalDataRefHook';
import { validateIntegrationDataSettings } from '../components/IntegrationBuilder/IntegrationBuilderSettingsContainer';
import * as envUtils from '../utils/envUtils';
import useFeaturePermissionsHook from './useFeaturePermissionsHook';
import { ApplicationModuleFeatureEnum } from '../utils/featureUtils';

const useDrawerStyles = makeStyles({
    integrationBuilderDrawerOpacity: {
        opacity: 0.95,
    },
});

const integrationBuilderStoreNamespace = 'integrationBuilder';

/**
 * Integration builder default states
 * @type {{functions: *[], isSettingsDrawerOpen: boolean, setVariablesData: *[], droppedIntegrationStep: {}, stepDrawerDimensions: {width: number, minWidth: number, maxWidth: number}, originalTriggerStepData: null, integrationTestData: {isError: null, data: null}, conditionalStepValidation: {}, integrationVariableModalState: {stepAction: string, variable: {}, open: boolean}, draggingIntegrationStepItem: boolean, selectFieldModalState: {stepAction: string, isRootSelectable: boolean, open: boolean}, triggerDropdownList: {}[], integrationStepCreatedMapping: {}, defaultStepParam: {}, apiTriggerPayloadDataFormat: string, parametersData: [{name: string, value: string, sourceStepId: null}], setCountersData: *[], hasAddedTriggerStep: boolean, deletingIntegrationBuilderIndex: null, computedStepsToRender: null, openTriggerDrawer: boolean, loadedIntegrationSteps: *[], deletingIntegrationBuilderStep: {}, isSubmittingIntegrationBuilderForm: boolean, hasStepValidationError: boolean, transformComponent: {stepItemWidth: number, scale: number}, resourcesDropDownOptions: *[], currentLocalIntegrationSteps: {nextSteps: [], stepType: string, stepId: number, freq: string, action: string, description: string, startTime: *, interval: string, prevSteps: [], monthlyInterval: string, triggerType: string}[], isDeleteStepNotAllowedModalOpen: boolean, openDisplayIntegrationTestModal: boolean, isDeleteStepModalOpen: boolean, selectedStepDimensions: {}, openIntegrationBuilderStepListDrawer: boolean, panning: boolean, integrationCounterModalState: {stepAction: string, counter: {}, open: boolean}, shouldHandleStepChanges: number, selectedAppInstanceApplicationData: {}, minimizeIntegrationLogOutput: boolean, variables: *[], apiCallTriggerModal: {open: boolean}, parameterDropDownOptions: *[], stepActions: *[], appRequestParameterDropDownOptions: *[], appInstances: *[], previousStepParameterPopupState: {updateParamValue: undefined, paramsData: null, index: null, step: null, open: boolean}, lastGeneratedStepId: number, selectedStepMetadata: {}, integrationStringInputModalState: {stringTypeOptions: *[], stringValue: string, onSubmit: integrationBuilderDefaultStates.integrationStringInputModalState.onSubmit, stringType: string, showOptionsForStringType: boolean, customValidation: integrationBuilderDefaultStates.integrationStringInputModalState.customValidation, open: boolean}, openStepDrawer: {step: number, state: boolean}, setVariableModalState: {variable: {}, isRootSelectable: boolean, open: boolean}}}
 */
export const integrationBuilderDefaultStates = {
    panning: false,
    stepActions: [],
    appInstances: [],
    openStepDrawer: { step: 0, state: false },
    parametersData: [{ name: '', value: '', sourceStepId: null }],
    defaultStepParam: {},
    stepDrawerDimensions: {
        width: constants.INTEGRATION_BUILDER_STEP_SIDEBAR_WIDTH,
        minWidth: constants.INTEGRATION_BUILDER_STEP_SIDEBAR_WIDTH,
        maxWidth: constants.INTEGRATION_BUILDER_STEP_SIDEBAR_MAX_WIDTH,
    },
    lastGeneratedStepId: 1,
    apiCallTriggerModal: { open: false },
    openTriggerDrawer: true,
    hasAddedTriggerStep: false,
    triggerDropdownList: getTriggerList(),
    originalTriggerStepData: null,
    selectedStepMetadata: {},
    isDeleteStepModalOpen: false,
    computedStepsToRender: null,
    hasStepValidationError: false,
    currentValidationErrors: [],
    loadedIntegrationSteps: [],
    shouldHandleStepChanges: 0,
    selectedStepDimensions: {},
    droppedIntegrationStep: {},
    resourcesDropDownOptions: [],
    setCountersData: [],
    parameterDropDownOptions: [],
    conditionalStepValidation: {},
    draggingIntegrationStepItem: false,
    apiTriggerPayloadDataFormat: 'json',
    currentLocalIntegrationSteps: getDefaultStepList(),
    minimizeIntegrationLogOutput: false,
    integrationStepCreatedMapping: {},
    deletingIntegrationBuilderStep: {},
    deletingIntegrationBuilderIndex: null,
    openDisplayIntegrationTestModal: false,
    // Result dialog shown after a Run triggers a background execution (Close / View Log).
    integrationExecutionStartedModal: { open: false, executionId: null },
    isDeleteStepNotAllowedModalOpen: false,
    appRequestParameterDropDownOptions: [],
    selectedAppInstanceApplicationData: {},
    isSubmittingIntegrationBuilderForm: false,
    openIntegrationBuilderStepListDrawer: true,
    isSettingsDrawerOpen: false,
    currentIntegrationMetaData: {},
    variables: [],
    transformComponent: {
        scale: 1,
        stepItemWidth: 200,
    },
    integrationTestData: {
        data: null,
        isError: null,
    },
    // True while an async test execution's log lines are being tailed live.
    isStreamingIntegrationLog: false,
    previousStepParameterPopupState: {
        open: false,
        step: null,
        paramsData: null,
        index: null,
        updateParamValue: undefined,
    },
    selectFieldModalState: {
        open: false,
        stepAction: '',
        isRootSelectable: false,
    },
    integrationVariableModalState: {
        open: false,
        variable: {},
        stepAction: '',
    },
    integrationCounterModalState: {
        open: false,
        counter: {},
        stepAction: '',
    },
    setVariableModalState: {
        open: false,
        variable: {},
        isRootSelectable: false,
    },
    functions: [],
    integrationStringInputModalState: {
        open: false,
        stringValue: '',
        showOptionsForStringType: false,
        showTreatAsTypeSelect: false,
        treatAsTypeValue: null,
        stringType: '',
        stringTypeOptions: [],
        customValidation: () => {},
        onSubmit: () => {},
        tooltip: null,
    },
    hasUserLeftPageAndReturned: false,
    integrationConditionalStepModalState: {
        isOpen: false,
        addConditionalInBetween: null,
        addConditionalWithStepsInBranch: null,
    },
};

/**
 * Transform components states
 */
export const transformComponentStates = {
    transformComponentPanState: {
        x: 0,
        y: 0,
        bottom: 50, // for use in bottom padding
    },
};

/**
 * Set an integration builder state
 * @param {String} state Integration builder state name
 * @param {*} defaultValue state State default value
 * @returns {Function<String, Function>}
 */
export const useIntegrationBuilderState = (state, defaultValue = undefined) =>
    useStoreState(state, integrationBuilderStoreNamespace)(defaultValue);

/**
 * Get an integration builder state value and subscribe to changes
 * @param {String} state Integration builder state name
 * @param {*} fallbackValue State fallback value
 * @returns {*}
 */
export const useIntegrationBuilderValue = (state, fallbackValue = undefined) =>
    useStoreValue(state, integrationBuilderStoreNamespace)(fallbackValue);

/**
 * Get the integration builder state values and subscribe to changes
 * @returns {Object} Integration builder state values
 */
export const useIntegrationBuilderValues = () => useStoreValues(integrationBuilderStoreNamespace);

/**
 * Get an integration builder state value
 * @param {String} state Integration builder state name
 * @param {*} fallbackValue State fallback value
 * @returns {*} State value
 */
export const getIntegrationBuilderState = (state, fallbackValue = undefined) =>
    getStoreState(state, integrationBuilderStoreNamespace)(fallbackValue);

/**
 * Get the integration builder state values
 * @returns {*} State value
 */
export const getIntegrationBuilderStates = () => getStoreStates(integrationBuilderStoreNamespace);

/**
 * Set an integration builder state value
 * @param {String} state Integration builder state name
 * @param {*} value State value
 */
export const setIntegrationBuilderState = (state, value) => setStoreState(state)(value);

/**
 * Transform component pan state
 * @returns {Array<object, function>}
 */
export const useTransformComponentPanState = () =>
    useStoreState('transformComponentPanState', 'transformComponentPan')({});

/**
 * Set integration builder state values
 * @param {Object} values New state values
 *
 * Note: values are automatically mapped to their respective action type.
 */
export const setIntegrationBuilderStates = (values) => setStoreStates(values);

/**
 * @typedef WebhookConfig
 * @property {string} method
 * @property {string} endpointUrl
 * @property {{key: string, value: Object}[]} queryParams
 * @property {{key: string, value: Object}[]} headers
 * @property {boolean} requiresAuthorization
 * @property {AuthorizationConfig} authorizationConfig
 */

/**
 * @typedef IntegrationDataResponseBodyIncludes
 * @property {string} responseOutputMode
 * @property {boolean} logs
 * @property {boolean} output
 * @property {boolean} errorData
 * @property {boolean} additionalData
 * @property {string} additionalDataModes
 * @property {{key: string, value: Object}[]} additionalDataFields
 * @property {string} additionalDataJson
 */

/**
 * @typedef IntegrationStepParameter
 * @property {string} type
 * @property {string?} name
 * @property {string?} value
 * @property {number?} sourceStepId
 * @property {Object?} sourceDataTypeInputPath
 * @property {Object?} sourceDataTypeInputPathFilters
 * @property {string?} sourceDisplayLabel
 * @property {string?} crosswalkId
 * @property {"value"|"length"?} sourceRetrieveValueType
 */

/**
 * @typedef IntegrationExecuteScriptStepParameter
 * @property {string} parameterId
 * @property {number?} sourceStepId
 * @property {string?} sourceDataTypeId
 * @property {Object?} sourceInputPath
 * @property {"value"|"length"?} sourceRetrieveValueType
 * @property {string?} sourceDisplayLabel
 * @property {Object[]?} sourceInputFilters
 * @property {string?} sourceInputDelimiter
 * @property {number?} sourceInputEntryOffset
 * @property {string?} rawData
 */

/**
 * @typedef IntegrationControlOptions
 * @property {Object} variable
 * @property {boolean} variable.delete
 * @property {boolean} variable.edit
 * @property {boolean} variable.add
 * @property {Object} counter
 * @property {boolean} counter.delete
 * @property {boolean} counter.edit
 * @property {boolean} counter.add
 */

/**
 * @typedef IntegrationStep
 * @property {"Trigger"|"VendorRequest"|"TestDataRequest"|"Convert"|"App Request"|"Log"|"Conditional"|"End Conditional"|"Loop"|"Exit"|"Break Loop"|"Continue Loop"|"Delay"|"Set Variable"|"Comparison"|"Increment/Decrement Counter"|"Run Integration"|"Split File"|"Combine Files"|"RESTful Request"|"Authorization"|"Crosswalk"|"Cast"} stepType
 * @property {number} stepId
 * @property {string?} description
 * @property {"normal"|"advanced"?} stepMode
 * @property {number[]?} prevSteps
 * @property {number[]?} nextSteps
 * @property {boolean} continueIfFailed
 * @property {boolean?} autoRetry
 * @property {number} numberOfRetries
 * @property {IntegrationStepParameter[]} parameters
 * @property {string?} vendorId
 * @property {string?} vendorInstanceId
 * @property {string?} action
 * @property {string?} resource
 * @property {"Scheduler"|"Manual"|"API"?} triggerType
 * @property {string?} startTime
 * @property {0|1?} freq
 * @property {0|1|2|3|4?} interval
 * @property {"first"|"middle"|"last"|"custom"?} monthlyInterval
 * @property {number?} triggerScheduleMinuteOffset
 * @property {number?} triggerScheduleHourOffset
 * @property {number?} triggerScheduleDayOffset
 * @property {number?} triggerScheduleWeekOffset
 * @property {{dayOfWeek: number[]?, occurrences: number[]?}?} triggerScheduleDaysOfTheWeek
 * @property {string?} triggerScheduleRunBetweenStartTime
 * @property {string?} triggerScheduleRunBetweenEndTime
 * @property {boolean?} rescheduleOnFailure
 * @property {string?} triggerDataTypeId
 * @property {string?} myAppApplicationId
 * @property {string?} myAppInstanceId
 * @property {string?} myAppInterfaceId
 * @property {string?} myAppResourceId
 * @property {string?} myAppActionId
 * @property {string?} requestBodyValueType
 * @property {number?} requestBodySourceStepId
 * @property {string?} requestBodyRawText
 * @property {string?} requestBodySourceDataTypeId
 * @property {[]?} requestBodySourceInputPath
 * @property {"value"|"length"?} requestBodySourceRetrieveValueType
 * @property {string?} requestBodySourceDisplayLabel
 * @property {Object?} requestBodyInputFilters
 * @property {string?} mappingId
 * @property {Object[]?} inputMappings
 * @property {string?} mappingOutputDelimiter
 * @property {boolean?} mappingWriteToFile
 * @property {boolean?} mappingIncludeHeaders
 * @property {string?} mappingConditionId
 * @property {number?} containerStepId
 * @property {IntegrationStep[]} containedSteps
 * @property {string?} conditionalType
 * @property {Object[]?} conditionList
 * @property {number?} conditionSourceStepIdForComparison
 * @property {string?} conditionSourceDataTypeIdForComparison
 * @property {string?} conditionSourceDataTypeDisplayLabelForComparison
 * @property {[]?} conditionSourceInputPathForComparison
 * @property {string?} conditionSourceRetrieveValueType
 * @property {number[]?} conditionElseNextSteps
 * @property {number?} conditionalEndStepId
 * @property {Object?} crosswalkData
 * @property {number?} sourceStepIdForLoop
 * @property {string?} sourceDataTypeIdForLoop
 * @property {string?} sourceDataTypeDisplayLabelForLoop
 * @property {boolean?} runIterationsInParallel
 * @property {[]?} sourceInputPathForLoop
 * @property {[]?} sourceFiltersForLoop
 * @property {string?} onLoopError
 * @property {string?} exitResultType
 * @property {string[]?} notifyEmailList
 * @property {string?} logType
 * @property {boolean?} includePreviousStepData
 * @property {string?} logMessage
 * @property {Object[]?} logFields
 * @property {number?} delayInMilliseconds
 * @property {Object[]?} variables
 * @property {Object[]?} counters
 * @property {string?} comparisonId
 * @property {string?} comparisonReturnType
 * @property {string?} comparisonReturnRecords
 * @property {boolean?} comparisonEvaluateExpressionsIndependently
 * @property {boolean?} comparisonWriteToFile
 * @property {string?} comparisonFileDestination
 * @property {Object?} comparisonFileDestinationReference
 * @property {boolean?} returnResultsCollapseExpressions
 * @property {Object[]?} comparisonInputs
 * @property {Object?} restfulAuthorization
 * @property {string?} integrationIdToRun
 * @property {string?} integrationDataValueType
 * @property {number?} integrationDataSourceStepId
 * @property {string?} integrationDataTypeId
 * @property {Object[]?} integrationDataSourceDataTypeInputPath
 * @property {string?} integrationDataRawText
 * @property {string?} integrationDataSourceDisplayLabel
 * @property {string?} integrationMode
 * @property {string?} restfulRequestStepMethod
 * @property {string?} restfulRequestStepUrl
 * @property {string?} restfulRequestStepReturnDataType
 * @property {string?} restfulRequestStepBodyRawValue
 * @property {string?} restfulRequestStepBodyContentType
 * @property {Object?} restfulRequestStepBody
 * @property {Object?} restfulRequestStepHeaders
 * @property {Object?} restfulRequestStepQueryParams
 * @property {Object?} relativeFilePath
 * @property {string?} typeOfFileSplit
 * @property {string?} splitWithHeaderRow
 * @property {string?} combineWithHeaderRow
 * @property {string?} byteSizePerFile
 * @property {string?} delimiter
 * @property {string?} recordCountPerFile
 * @property {string?} typeOfFileCombine
 * @property {Object[]?} sourceFilePaths
 * @property {Object?} destinationFolderPath
 * @property {Object?} fileName
 * @property {boolean?} replaceExistingFile
 * @property {string?} fileCombineDelimiter
 * @property {Object?} advancedOptions
 * @property {string?} executeScriptId
 * @property {IntegrationExecuteScriptStepParameter[]?} executeScriptParameters
 * @property {CastOptions?} castOptions
 */

/**
 * @typedef CastOptions
 * @property {number?} sourceStepId
 * @property {string?} sourceDataTypeId
 * @property {[]?} sourceInputPath
 * @property {string?} sourceDisplayLabel
 * @property {string?} sourceInputDelimiter
 * @property {number?} sourceInputEntryOffset
 * @property {string?} crosswalkId
 * @property {string?} rawData
 * @property {string?} castAsDataTypeId
 */

/**
 * @typedef Version
 * @property {number} id
 * @property {number|null} publishedId
 * @property {string} documentId
 * @property {string} name
 */

/**
 * @typedef IntegrationData
 * @property {string} _id
 * @property {string} name
 * @property {string} description
 * @property {IntegrationStep[]} steps
 * @property {string} organizationId
 * @property {string} userId
 * @property {number} status
 * @property {boolean} isTemplate
 * @property {string} deletedAt
 * @property {string} runTime
 * @property {boolean} isRunning
 * @property {boolean} isTest
 * @property {[]} variables
 * @property {[]} counters
 * @property {boolean} sendNotifications
 * @property {string[]} notificationEmailList
 * @property {IntegrationDataResponseBodyIncludes} responseBodyIncludes
 * @property {boolean} webhookAfterExecution
 * @property {WebhookConfig} webhookConfig
 * @property {Version} version
 */

/**
 * @typedef IntegrationBuilderProps
 * @property {Object} classes
 * @property {MutableRefObject} zoomInRef
 * @property {MutableRefObject} zoomOutRef
 * @property {MutableRefObject} nextStepIdRef
 * @property {MutableRefObject} indexCounterRef
 * @property {MutableRefObject} highestStepIdRef
 * @property {MutableRefObject} stepsCacheListRef
 * @property {MutableRefObject} stepCounterListRef
 * @property {MutableRefObject} lastDeletedStepIdRef
 * @property {MutableRefObject} saveIntegrationButtonRef
 * @property {MutableRefObject} droppedStepActionCounterRef
 * @property {MutableRefObject} transformComponentElementRef
 * @property {MutableRefObject} stepReferencedInOtherStepsRef
 * @property {Object} stepTypes
 * @property {boolean} isDataLoaded
 * @property {Object} drawerStyles
 * @property {Object} enabledStepTypes
 * @property {Object} defaultStepParam
 * @property {RegExp} parameterDataRegex
 * @property {Array<Object>} triggerDropdownList
 * @property {IntegrationData} selectedIntegrationData
 * @property {Array} inputMappingsDefaultState
 * @property {IntegrationControlOptions} integrationControlOptions
 *
 * @property {function} setIntegrationExecuteMode
 * @property {function} getStepLabel
 * @property {function} setUnsavedDataState
 * @property {function} setupStepDefaultData
 * @property {function} handleTestIntegration
 * @property {function} executeTestIntegration
 * @property {function} executeManualIntegration
 * @property {function} validateIntegrationSteps
 * @property {function} hasUnsavedIntegrationData
 * @property {function} addIntegrationBuilderStep
 * @property {function} saveIntegrationBuilderData
 * @property {function} updateLocalIntegrationSteps
 * @property {function} handleOpenFieldSelectorModal
 * @property {function} removeIntegrationBuilderStep
 * @property {function} addNewIntegrationBuilderStep
 * @property {function} handleNavigateToDataTypeScreen
 * @property {function} getTransformComponentZoomStyles
 * @property {function} handleOpenIntegrationVariableModal
 * @property {function} handleIntegrationVariableChanges
 * @property {function} handleOpenIntegrationStringInputModal
 * @property {function} handleOpenIntegrationCounterModal
 * @property {function} handleIntegrationCounterChanges
 */

/**
 * Integration builder state data setup.
 *
 * Note: {@see useSelectedIntegrationData()} is used to synchronize the
 * RenameIntegrationModal component with the IntegrationBuilder.
 *
 * @returns {IntegrationBuilderProps}
 */
export const useIntegrationBuilderEffect = () => {
    const { integrationId } = useParams(),
        classes = useStyles()(),
        drawerStyles = useDrawerStyles(),
        stepTypes = getStepTypes(classes)?.filter((step) => step?.isHidden !== true),
        enabledStepTypes = getEnabledStepTypes(),
        defaultStepParam = getDefaultStepObject(),
        parameterDataRegex = new RegExp(/^{S(\d):(.*)}$/),
        triggerDropdownList = getTriggerList(),
        selectedIntegrationData = useSelectedIntegrationData(),
        { setUnsavedDataState, clearUnsavedDataState } = useWatchUnsavedDataContext(),
        zoomInRef = useRef(null),
        zoomOutRef = useRef(null),
        indexCounterRef = useRef(0),
        stepCounterListRef = useRef({}),
        saveIntegrationButtonRef = useRef(null),
        transformComponentElementRef = useRef(null),
        invalidStepValidationCounterRef = useRef(0),
        stepsCacheListRef = useRef({}),
        droppedStepActionCounterRef = useRef(0),
        stepReferencedInOtherStepsRef = useRef({}),
        highestStepIdRef = useRef(1),
        [isDataLoaded, setIsDataLoaded] = useState(false),
        lastDeletedStepIdRef = useRef(null),
        nextStepIdRef = useRef(null),
        getGlobalDataRefItem = useGetGlobalRefItem(),
        setGlobalDataRefItem = useSetGlobalRefItem(),
        removeGlobalDataRefItem = useRemoveGlobalRefItem(),
        [isIntegrationEditable, setIsEditable] = useState(false);

    const [isAbleToEditIntegration] = useFeaturePermissionsHook({
        featureList: [ApplicationModuleFeatureEnum.INTEGRATIONS_EDIT],
    });

    const integrationControlOptions = useMemo(() => {
        // In future this maybe even more dynamic and computed
        return {
            // Variable
            variable: {
                delete: isIntegrationEditable,
                edit: true,
                add: isIntegrationEditable,
            },

            // Counter
            counter: {
                delete: isIntegrationEditable,
                edit: true,
                add: isIntegrationEditable,
            },

            // Update integration,
            // for now keep it simple since counters/variables can always be updated
            canUpdateIntegration: true,
        };
    }, [isIntegrationEditable]);

    /**
     * Load the integration data and related organization vendor instances
     */
    useEffect(() => {
        // Reset integration builder states
        setIntegrationBuilderStates(integrationBuilderDefaultStates);

        const initIntegrationBuilderData = () => {
            loadIntegrationData(integrationId);
            loadAppInstances();
            loadMappings();
            loadCrosswalks();
            loadComparisons();
            loadFunctions();
        };

        initIntegrationBuilderData();
        updateLocalIntegration({}); // Reset loaded integration

        return () => {
            updateLocalIntegration({}); // Reset loaded integration
        };
    }, []);

    /**
     * Determine whether to pre-open trigger details in TriggerDrawer
     */
    useEffect(() => {
        const { hasAddedTriggerStep, currentLocalIntegrationSteps } = getIntegrationBuilderStates();

        if (
            !hasAddedTriggerStep &&
            currentLocalIntegrationSteps?.length > 0 &&
            currentLocalIntegrationSteps?.[0]?.triggerType
        ) {
            setIntegrationBuilderStates({
                hasAddedTriggerStep: true,
            });
        }

        return () => {
            setIntegrationBuilderStates({
                hasAddedTriggerStep: false,
            });
        };
    }, [isDataLoaded]);

    /**
     * Update isIntegrationEditable on change of integration/admin status
     */
    useEffect(() => {
        setIsEditable(selectedIntegrationData?.version?.publishedId === null && isAbleToEditIntegration);
    }, [selectedIntegrationData]);

    /**
     * Load app instances data
     */
    const loadAppInstances = async () => {
        getOrganizationAppInstances({
            successCallback: (res) => {
                setIntegrationBuilderStates({
                    appInstances: res.data.results,
                });
            },
            errorCallback: () => {
                setIntegrationBuilderStates({
                    appInstances: [],
                });
            },
        });
    };

    /**
     * Load mappings
     */
    const loadMappings = async () => {
        const response = await getAllMappings();
        setMappingsData(response.results);
    };

    /**
     * Load crosswalks
     */
    const loadCrosswalks = async () => {
        const response = await getAllCrosswalks();
        setCrosswalks(response.results);
    };

    const loadComparisons = async () => {
        const response = await getAllComparisons();
        setComparisons(response.results);
    };

    /**
     * Load integration data from data source.
     * @param {String} integrationIdToLoad
     * @returns {Promise<void>}
     */
    const loadIntegrationData = async (integrationIdToLoad) => {
        return getIntegrationById(integrationIdToLoad)
            .then((data) => {
                if (data) {
                    updateLocalIntegration(data, true);

                    const triggerStepData = data?.steps?.find((step) => step.stepType === constants.STEP_TYPE_TRIGGER);
                    setOriginalTriggerStepData(triggerStepData);
                } else {
                    updateLocalIntegration({}, true);
                    showSnackBarErrorNotification(
                        'An error occurred attempting to load integration data. No integration data found to load.',
                    );
                }
            })
            .catch(() => {
                updateLocalIntegration({}, true);
                showSnackBarErrorNotification('An error occurred attempting to load integration data.');
            })
            .finally(() => {
                setIsDataLoaded(true);
            });
    };

    /**
     * Load functions
     */
    const loadFunctions = async () => {
        const response = await getAllFunctionTypes();
        setIntegrationBuilderStates({
            functions: response,
        });
    };

    /**
     * Update the current integration's data displayed.
     * @param {Object} integrationData
     * @param {Boolean} isLoaded
     */
    const updateLocalIntegration = (integrationData, isLoaded = false) => {
        setSelectedIntegrationData(integrationData);

        const translatedIntegrationData = getTranslatedStepDataFromIntegrationData(integrationData);

        updateLocalIntegrationSteps(translatedIntegrationData, isLoaded);
    };

    /**
     * Update the list of steps currently displayed for this integration.
     * @param {Array<object>} steps
     * @param {Bool} isLoaded
     * @param {object} states
     */
    const updateLocalIntegrationSteps = (steps, isLoaded = false, states = {}) => {
        const stepStates = {
            currentLocalIntegrationSteps: steps,
            isSubmittingIntegrationBuilderForm: false,
            ...states,
        };

        if (isLoaded) {
            stepStates.loadedIntegrationSteps = [...steps];
        }

        // Clear error if one of the step field is changed
        const hasStepValidationError = getIntegrationBuilderState('hasStepValidationError'),
            selectedStepMetadata = getIntegrationBuilderState('selectedStepMetadata', {});
        if (hasStepValidationError && selectedStepMetadata?.stepId === hasStepValidationError) {
            stepStates.hasStepValidationError = false;
        }

        setIntegrationBuilderStates(stepStates);
    };

    /**
     * Setup default step data
     * @param {object} step
     * @return {object} Mutated step object
     */
    const setupStepDefaultData = (step) => {
        const stepType = step?.stepType;

        switch (stepType) {
            case constants.STEP_TYPE_LOG:
                step.logType = 'Info';
                step.includePreviousStepData = false;
                break;
            case constants.STEP_TYPE_APP_REQUEST:
                step.requestBodyValueType = constants.STEP_REQUEST_BODY_VALUE_TYPE_NONE;
                break;
            case constants.STEP_TYPE_CONDITIONAL:
                step.nextSteps = [];
        }

        return step;
    };

    /**
     * @see addIntegrationBuilderStep()
     * Add new integration builder step
     * @param {Object} args
     * @param {Array<object>} args.steps
     * @param {object} args.stepData
     * @param {object} args.newStepData
     * @param {boolean} args.isAddStepBeforeLoopStep
     * @returns {{ integrationSteps: object, }}
     */
    const addNewIntegrationBuilderStep = ({ stepData, newStepData, steps, isAddStepBeforeLoopStep = false }) => {
        const currentLocalIntegrationSteps = steps || getIntegrationBuilderState('currentLocalIntegrationSteps'),
            integrationSteps = _.cloneDeep(currentLocalIntegrationSteps),
            stepId = stepData?.stepId,
            nextStepId = stepData?.nextSteps?.[0],
            isNewStepTypeConditional = newStepData.stepType === constants.STEP_TYPE_CONDITIONAL;

        if (!isIntegrationEditable) return { integrationSteps: currentLocalIntegrationSteps };

        let nextGeneratedStepId = getLastGeneratedStepId();
        if (highestStepIdRef.current > nextGeneratedStepId) {
            nextGeneratedStepId = highestStepIdRef.current;
        }
        nextGeneratedStepId += 1;

        if (nextStepIdRef.current) {
            nextGeneratedStepId = nextStepIdRef.current + 1;
        }

        const defaultStepData = setupStepDefaultData({
            ...defaultStepParam,
            stepId: nextGeneratedStepId,
            prevSteps: [stepId],
            ...newStepData,
        });

        if (isNewStepTypeConditional) {
            defaultStepData.conditionalEndStepId = nextGeneratedStepId + 1;
            // If step is dropped between steps, correctly set the next step ID
            if (nextStepId) {
                defaultStepData.conditionalType = constants.STEP_CONDITIONAL_TYPE_DEFAULT;
                defaultStepData.conditionList = [
                    {
                        name: constants.STEP_CONDITIONAL_NAME_DEFAULT,
                        nextSteps:
                            nextStepId && nextStepId !== stepData.conditionalContainerEndStepId ? [nextStepId] : [],
                        expressionList: [],
                    },
                ];
            }
        } else if (newStepData.stepType === constants.STEP_TYPE_LOOP) {
            defaultStepData.containedSteps = [];
        }

        if (!isNewStepTypeConditional) {
            defaultStepData.nextSteps = [nextStepId];
        }

        // Move first step position for loop step types
        if (isAddStepBeforeLoopStep) {
            const firstStepInLoop = integrationSteps?.[0] || {},
                hasFirstStep = firstStepInLoop.stepId !== undefined;

            defaultStepData.prevSteps = [];
            defaultStepData.nextSteps = hasFirstStep ? [firstStepInLoop.stepId] : [];

            integrationSteps.splice(0, 1, defaultStepData); // replace first step

            // Re-add the previous first step
            if (firstStepInLoop.stepId) {
                firstStepInLoop.prevSteps = [defaultStepData.stepId];
                integrationSteps.splice(1, 0, firstStepInLoop);
            }
        } else {
            integrationSteps.push(defaultStepData);
        }

        let addedStep = null;

        integrationSteps.forEach((item, index) => {
            const integrationStep = { ...item };

            if (
                integrationStep.stepId === nextStepId &&
                integrationStep.stepType !== constants.STEP_TYPE_END_CONDITIONAL
            ) {
                integrationStep.prevSteps = [defaultStepData.stepId];
            } else if (
                integrationStep.stepId === stepId &&
                integrationStep.stepType !== constants.STEP_TYPE_CONDITIONAL
            ) {
                integrationStep.nextSteps = [defaultStepData.stepId];
            }

            if (!addedStep && defaultStepData.stepId === integrationStep.stepId) {
                addedStep = integrationStep;
            }

            // Remove nextSteps from main conditional step object
            if (integrationStep.stepType === constants.STEP_TYPE_CONDITIONAL) {
                integrationStep.nextSteps = [];
            }

            integrationSteps[index] = integrationStep;
        });

        setIntegrationBuilderState('lastGeneratedStepId', nextGeneratedStepId);
        nextStepIdRef.current = null;

        return {
            addedStep,
            integrationSteps,
        };
    };

    /**
     * Add integration builder step
     * @param {Object} args
     * @param {string} args.stepData Adjacent step data.
     * @param {string} args.newStepData New step data.
     */
    const addIntegrationBuilderStep = ({ stepData, newStepData }) => {
        if (!isIntegrationEditable) return;
        const { integrationSteps } = addNewIntegrationBuilderStep({ stepData, newStepData });

        updateLocalIntegrationSteps(integrationSteps);
        setUnsavedDataState();
    };

    /**
     * Remove a step from the integrations step list
     * @param {Object} args
     * @param {object} args.step Integration step data to remove
     * @param {object} args.topLevelLoopStepData Integration steps
     * @param {number} args.conditionalStepIndex Conditional list index to delete
     */
    const removeIntegrationBuilderStep = async ({
        step,
        conditionalStepIndex,
        topLevelLoopStepData: _topLevelLoopStepData,
    }) => {
        if (!step || step?.stepId === undefined) return;

        const currentLocalIntegrationSteps = getIntegrationBuilderState('currentLocalIntegrationSteps');
        let mutatedSteps = [];

        /**
         * Remove the selected step
         * @param {{step object, steps: Array<object>}} args
         * @return {Array<object>}
         */
        const handleRemoveIntegrationStep = ({ step: stepToDelete, steps }) => {
            if (!isIntegrationEditable) return;

            const isStepToDeleteConditionalStep = stepToDelete?.stepType === constants.STEP_TYPE_CONDITIONAL;

            // Handle conditional step mutations
            if (step.isInConditional) {
                if (isStepToDeleteConditionalStep) {
                    const removedEndConditionalStepRef = steps.find(
                        (stepObj) => stepObj.stepId === stepToDelete.conditionalEndStepId,
                    );
                    const conditionalStepIsLastStepInBranch =
                        removedEndConditionalStepRef.nextSteps[0] ===
                        removedEndConditionalStepRef.conditionalContainerEndStepId;
                    const conditionalIsFirstStepInBranch =
                        stepToDelete.prevSteps[0] === stepToDelete.conditionalContainerStepId;
                    const parentConditionalStepRef = steps.find(
                        (stepObj) => stepObj.stepId === stepToDelete.conditionalContainerStepId,
                    );

                    // Conditional step is the only step in the branch
                    if (conditionalIsFirstStepInBranch && conditionalStepIsLastStepInBranch) {
                        parentConditionalStepRef.conditionList[conditionalStepIndex].nextSteps = [];
                    } else {
                        parentConditionalStepRef.conditionList[conditionalStepIndex].nextSteps =
                            removedEndConditionalStepRef.nextSteps || [];
                    }

                    if (conditionalStepIsLastStepInBranch) {
                        const parentEndConditionalStepRef = steps.find(
                            (stepObj) => stepObj.stepId === removedEndConditionalStepRef.conditionalContainerEndStepId,
                        );

                        if (parentEndConditionalStepRef) {
                            const index = parentEndConditionalStepRef.prevSteps.indexOf(
                                stepToDelete.conditionalEndStepId,
                            );
                            if (index > -1) {
                                !conditionalIsFirstStepInBranch
                                    ? parentEndConditionalStepRef.prevSteps.splice(
                                          parentEndConditionalStepRef.prevSteps.indexOf(
                                              stepToDelete.conditionalEndStepId,
                                          ),
                                          1,
                                          stepToDelete.prevSteps[0],
                                      )
                                    : parentEndConditionalStepRef.prevSteps.splice(index, 1);
                            }
                        }
                    }
                } else if (stepToDelete.nextSteps[0] === stepToDelete.conditionalContainerEndStepId) {
                    const parentEndConditionalStepRef = steps.find(
                        (stepObj) => stepObj.stepId === stepToDelete.conditionalContainerEndStepId,
                    );

                    if (parentEndConditionalStepRef) {
                        parentEndConditionalStepRef.prevSteps.splice(
                            parentEndConditionalStepRef.prevSteps.indexOf(stepToDelete.conditionalEndStepId),
                            1,
                            stepToDelete.prevSteps[0],
                        );
                    }
                }
            }

            const stepList = [],
                deletedStepPrevStepId = stepToDelete.prevSteps?.[0],
                deletedStepNextStepId = stepToDelete.nextSteps?.[0],
                endConditionalStepId = isStepToDeleteConditionalStep ? stepToDelete.conditionalEndStepId : null,
                // Get all related step IDs in deleted conditional step branches
                relatedStepIds = isStepToDeleteConditionalStep ? getConditionalStepRelatedNextStepsIds({ step }) : [];

            for (let i = 0; i < steps.length; i++) {
                const stepData = steps[i],
                    stepDataId = stepData?.stepId;

                if (
                    stepDataId === stepToDelete.stepId ||
                    relatedStepIds?.includes(stepDataId) ||
                    stepDataId === endConditionalStepId ||
                    stepData === undefined
                ) {
                    continue;
                }

                if (stepDataId === deletedStepPrevStepId) {
                    if (stepData.stepType !== constants.STEP_TYPE_CONDITIONAL) {
                        stepData.nextSteps = [deletedStepNextStepId];
                    }
                } else if (stepDataId === deletedStepNextStepId) {
                    if (stepData.stepType !== constants.STEP_TYPE_END_CONDITIONAL) {
                        stepData.prevSteps = [deletedStepPrevStepId];
                    }
                }

                stepList.push(stepData);
            }

            mutatedSteps = _.cloneDeep(stepList);
            return mutatedSteps;
        };

        let newSteps;

        if (_topLevelLoopStepData && step?.containerStepId !== undefined) {
            const { steps, topLevelLoopStepData } = getMutableIntegrationStepDeep({
                stepId: step.stepId,
                topLevelLoopStepData: _topLevelLoopStepData,
                callback: handleRemoveIntegrationStep,
            });

            const deletedStepParentContainedStepData = getMutableIntegrationStepDeep({
                steps,
                stepId: step.containerStepId,
                topLevelLoopStepData,
                callback: ({ step }) => {
                    step.containedSteps = mutatedSteps;
                },
            });

            newSteps = getMutableIntegrationStepByStepId(
                {
                    steps: deletedStepParentContainedStepData.steps,
                    stepId: _topLevelLoopStepData.stepId,
                },
                ({ steps, index }) => {
                    steps[index] = deletedStepParentContainedStepData.topLevelLoopStepData;
                },
            );
        } else {
            newSteps = handleRemoveIntegrationStep({
                step,
                steps: currentLocalIntegrationSteps,
            });
        }

        if (!newSteps) return;

        let lastGeneratedStepId = getLastGeneratedStepId();
        if (lastGeneratedStepId === step.stepId) {
            lastGeneratedStepId -= 1;
            nextStepIdRef.current = lastGeneratedStepId;
            highestStepIdRef.current = lastGeneratedStepId;
        } else {
            nextStepIdRef.current = null;
        }

        lastDeletedStepIdRef.current = step.stepId;

        updateLocalIntegrationSteps(newSteps, false, {
            lastGeneratedStepId,
            openStepDrawer: { state: false },
            openTriggerDrawer: true,
            // selectedStepMetadata: filterIntegrationStepMetadataByStepId(newStepList, getTriggerStepData()),
            selectedStepMetadata: {},
        });
    };

    /**
     * Prunes fields that should not be saved over the existing data.
     * @param {IntegrationData} currentIntegrationMetaData
     */
    const clearSpecificIntegrationMetaDataFields = (currentIntegrationMetaData) => {
        delete currentIntegrationMetaData._id;
        delete currentIntegrationMetaData.organizationId;
        delete currentIntegrationMetaData.userId;
        delete currentIntegrationMetaData.steps;
        delete currentIntegrationMetaData.variables;
        delete currentIntegrationMetaData.counters;
        delete currentIntegrationMetaData.isTemplate;
        delete currentIntegrationMetaData.deletedAt;
        delete currentIntegrationMetaData.runTime;
        delete currentIntegrationMetaData.isRunning;
        delete currentIntegrationMetaData.isTest;
    };

    /**
     * Save the current local integration data to our data source.
     * @returns {Promise<void>}
     */
    const saveIntegrationBuilderData = async () => {
        const currentLocalIntegrationSteps = getIntegrationBuilderState('currentLocalIntegrationSteps'),
            updatedIntegrationMetaData = getIntegrationBuilderState('currentIntegrationMetaData');
        clearSpecificIntegrationMetaDataFields(updatedIntegrationMetaData);

        if (!currentLocalIntegrationSteps?.length > 0 || !integrationId) {
            envUtils.logError('failed to save integration - steps or id invalid');
            return;
        }

        if (validateIntegrationTriggerChanges()) {
            setIntegrationBuilderStates({
                isSubmittingIntegrationBuilderForm: true,
            });

            const normalizeStepsData = normalizeIntegrationStepsData({ steps: currentLocalIntegrationSteps });

            return updateIntegration(
                {
                    ...selectedIntegrationData,
                    // steps: getTranslatedStepListForSave(currentLocalIntegrationSteps),
                    steps: normalizeStepsData,
                },
                updatedIntegrationMetaData,
            )
                .then((data) => {
                    if (data) {
                        showSnackBarSuccessNotification('Integration Updated Successfully');
                        updateLocalIntegration(data);
                        clearUnsavedDataState();
                        updateLocalIntegration(data, true);
                        setOriginalTriggerStepData(
                            data.steps.find((step) => step.stepType === constants.STEP_TYPE_TRIGGER),
                        );
                        updateIntegrationsOptimistically('update', data);
                    } else {
                        const loadedIntegrationSteps = getIntegrationBuilderState(
                            'loadedIntegrationSteps',
                            getDefaultStepList(),
                        );
                        updateLocalIntegrationSteps(loadedIntegrationSteps, true);
                        showSnackBarErrorNotification(
                            'A data error occurred attempting to save the current integration.',
                        );
                    }
                })
                .catch((error) => {
                    showSnackBarErrorNotification(
                        'An application error occurred attempting to save the current integration.',
                    );
                    envUtils.logError(error);
                })
                .finally(() => {
                    setIntegrationBuilderStates({
                        hasStepValidationError: false,
                        isSubmittingIntegrationBuilderForm: false,
                    });
                });
        } else {
            envUtils.logError('unable to validate trigger step details');
        }
    };

    /**
     *
     * @returns {boolean}
     */
    const validateIntegrationTriggerChanges = () => {
        const originalTriggerData = getOriginalTriggerStepData(),
            currentStepList = getIntegrationBuilderState('currentLocalIntegrationSteps'),
            currentTriggerData = currentStepList.find((step) => step.stepType === constants.STEP_TYPE_TRIGGER);

        let numberOfErrors = 0;

        const hasChangedTriggerScheduleProperties =
            currentTriggerData.triggerType === constants.TRIGGER_TYPE_SCHEDULER &&
            (originalTriggerData?.startTime?.toString() !== currentTriggerData.startTime.toString() ||
                originalTriggerData?.freq !== currentTriggerData.freq ||
                originalTriggerData?.triggerScheduleMinuteOffset !== currentTriggerData.triggerScheduleMinuteOffset ||
                originalTriggerData?.triggerScheduleHourOffset !== currentTriggerData.triggerScheduleHourOffset ||
                originalTriggerData?.triggerScheduleDayOffset !== currentTriggerData.triggerScheduleDayOffset ||
                originalTriggerData?.triggerScheduleWeekOffset !== currentTriggerData.triggerScheduleWeekOffset ||
                originalTriggerData?.triggerScheduleRunBetweenStartTime !==
                    currentTriggerData.triggerScheduleRunBetweenStartTime ||
                originalTriggerData?.triggerScheduleRunBetweenEndTime !==
                    currentTriggerData.triggerScheduleRunBetweenEndTime ||
                // days of the week changes
                !currentTriggerData.triggerScheduleDaysOfTheWeek?.daysOfWeek.every(
                    (value, index) => value === originalTriggerData?.triggerScheduleDaysOfTheWeek?.daysOfWeek[index],
                ) ||
                !originalTriggerData.triggerScheduleDaysOfTheWeek?.daysOfWeek.every(
                    (value, index) => value === currentTriggerData?.triggerScheduleDaysOfTheWeek?.daysOfWeek[index],
                ) ||
                // occurrences changes
                !currentTriggerData.triggerScheduleDaysOfTheWeek?.occurrences.every(
                    (value, index) => value === originalTriggerData?.triggerScheduleDaysOfTheWeek?.occurrences[index],
                ) ||
                !originalTriggerData.triggerScheduleDaysOfTheWeek?.occurrences.every(
                    (value, index) => value === currentTriggerData?.triggerScheduleDaysOfTheWeek?.occurrences[index],
                ));

        if (hasChangedTriggerScheduleProperties) {
            const startTimeDayJs = dayjs(currentTriggerData.startTime);
            if (startTimeDayJs.isBefore(dayjs(), 'date')) {
                showSnackBarErrorNotification("The trigger's scheduled start time must on or after today's date.");
                numberOfErrors++;
            }
        }

        if (currentTriggerData?.interval === constants.TRIGGER_SCHEDULER_INTERVAL_CUSTOM) {
            let hasOffsetError = false;
            if (!isNumber(currentTriggerData.triggerScheduleMinuteOffset)) {
                numberOfErrors++;
                hasOffsetError = true;
            }

            if (!isNumber(currentTriggerData.triggerScheduleHourOffset)) {
                numberOfErrors++;
                hasOffsetError = true;
            }

            if (!isNumber(currentTriggerData.triggerScheduleDayOffset)) {
                numberOfErrors++;
                hasOffsetError = true;
            }

            if (!isNumber(currentTriggerData.triggerScheduleWeekOffset)) {
                numberOfErrors++;
                hasOffsetError = true;
            }

            if (hasOffsetError) {
                showSnackBarErrorNotification(
                    'The trigger for this integration has a custom interval set with invalid offsets.',
                );
            }
        }

        if (
            (currentTriggerData.triggerScheduleRunBetweenStartTime &&
                !currentTriggerData.triggerScheduleRunBetweenEndTime) ||
            (currentTriggerData.triggerScheduleRunBetweenEndTime &&
                !currentTriggerData.triggerScheduleRunBetweenStartTime)
        ) {
            numberOfErrors++;
            showSnackBarErrorNotification(
                'The trigger step requires both a run between start and end time if either is set.',
            );
        } else if (
            currentTriggerData.triggerScheduleRunBetweenStartTime &&
            currentTriggerData.triggerScheduleRunBetweenEndTime
        ) {
            if (
                dayjs(currentTriggerData.triggerScheduleRunBetweenStartTime).isAfter(
                    currentTriggerData.triggerScheduleRunBetweenEndTime,
                )
            ) {
                numberOfErrors++;
                showSnackBarErrorNotification(
                    'The trigger step requires a run between start time that is before the end time.',
                );
            } else if (
                !currentTriggerData.triggerScheduleMinuteOffset &&
                !currentTriggerData.triggerScheduleHourOffset
            ) {
                numberOfErrors++;
                showSnackBarErrorNotification(
                    'The trigger step requires a minute or hour offset when a run between time is set.',
                );
            }
        }

        return numberOfErrors < 1;
    };

    /**
     * Run validation rules against the current integration step data and
     * open the selected drawer with errors.
     *
     * @returns {Promise<boolean>} True if steps are valid. Otherwise, false.
     */
    const validateIntegrationSteps = async () => {
        invalidStepValidationCounterRef.current = 0;
        const { isValid } = await traverseStepsForValidation({
            steps: getIntegrationBuilderState('currentLocalIntegrationSteps'),
            invalidStepValidationCounterRef,
        });

        return isValid;
    };

    /**
     * Execute selected integration
     * @returns {Promise<void>}
     */
    const executeManualIntegration = async () => {
        const integrationMetaData = getIntegrationBuilderState('currentIntegrationMetaData');

        setIntegrationBuilderStates({
            isSubmittingIntegrationBuilderForm: true,
        });

        if (!validateIntegrationDataSettings(integrationMetaData, selectedIntegrationData)) return;

        if (!(await validateIntegrationSteps())) return;

        // The run now triggers a background execution and surfaces a result dialog (Close / View Log)
        // — the log is viewed in the UI's execution log viewer rather than an inline Connect viewer.
        await testIntegration();

        setIntegrationBuilderStates({
            isSubmittingIntegrationBuilderForm: false,
        });

        // Update integration after execution if steps contain a set variable step
        const currentLocalIntegrationSteps = getIntegrationBuilderState('currentLocalIntegrationSteps'),
            hasSetVariableStep = currentLocalIntegrationSteps?.find(
                (step) => step.stepType === constants.STEP_TYPE_SET_VARIABLE,
            );

        if (hasSetVariableStep) {
            await getIntegrationById(integrationId)
                .then((data) => {
                    if (data) {
                        updateLocalIntegration(data, true);
                    } else {
                        updateLocalIntegration({}, true);
                    }
                })
                .catch(() => {
                    updateLocalIntegration({}, true);
                });
        }
    };

    /**
     * Fields on `currentIntegrationMetaData` that must NOT count as unsaved settings edits.
     *
     * `steps` is covered by {@link hasUnsavedIntegrationData}; `variables`/`counters` are edited through their
     * own dialogs; the rest are server-owned or change without user action, so diffing them would report a
     * dirty state the user never created.
     */
    const METADATA_FIELDS_EXCLUDED_FROM_DIRTY_CHECK = new Set([
        'steps',
        'variables',
        'counters',
        'version',
        'status',
        'runTime',
        'latestRunTime',
        'updatedAt',
        'updatedBy',
        'createdAt',
        'createdBy',
        '_id',
        'organizationId',
        'userId',
    ]);

    /**
     * Determine if the settings drawer holds unsaved metadata edits (webhook, notifications, parallel runs,
     * name/description and so on).
     *
     * Needed because {@link hasUnsavedIntegrationData} only compares workflow STEPS, so a session that changed
     * nothing but Integration Settings looked clean — and guards built on it let those edits be discarded with
     * no confirmation.
     *
     * `currentIntegrationMetaData` is seeded as a copy of the loaded integration when the settings drawer
     * mounts, so the loaded integration is the correct baseline, and an empty object means the drawer was never
     * opened (nothing to compare). Values are compared serialized because several of these fields are objects
     * or arrays.
     * @returns {Boolean}
     */
    const hasUnsavedIntegrationMetaData = () => {
        const currentIntegrationMetaData = getIntegrationBuilderState('currentIntegrationMetaData');

        if (!currentIntegrationMetaData || Object.keys(currentIntegrationMetaData).length === 0) {
            return false;
        }

        return Object.keys(currentIntegrationMetaData).some((fieldName) => {
            if (METADATA_FIELDS_EXCLUDED_FROM_DIRTY_CHECK.has(fieldName)) {
                return false;
            }

            return (
                JSON.stringify(currentIntegrationMetaData[fieldName]) !==
                JSON.stringify(selectedIntegrationData?.[fieldName])
            );
        });
    };

    /**
     * Determine if there is any unsaved integration data in the
     * current workflow.
     * @returns {Boolean}
     */
    const hasUnsavedIntegrationData = () => {
        const { loadedIntegrationSteps, currentLocalIntegrationSteps } = getIntegrationBuilderStates();

        if (loadedIntegrationSteps.length !== currentLocalIntegrationSteps.length) {
            return true;
        }

        /**
         * Compare loaded integration steps with current integration steps data for changes
         */
        let unsavedDataCounter = 0;
        currentLocalIntegrationSteps.forEach((stepData, i) => {
            const loadedStepData = loadedIntegrationSteps[i],
                stepDataParameters = stepData?.parameters || [],
                loadedStepDataParameters = loadedStepData?.parameters || [];

            if (
                stepData?.vendorId !== loadedStepData?.vendorId ||
                stepData?.resource !== loadedStepData?.resource ||
                stepData?.action !== loadedStepData?.action ||
                stepDataParameters?.length !== loadedStepDataParameters?.length
            ) {
                ++unsavedDataCounter;
                return true;
            }

            stepDataParameters.forEach((parameter, index) => {
                const loadedStepParameter = loadedStepDataParameters[index];

                if (
                    parameter?.name !== loadedStepParameter?.name ||
                    parameter?.value !== loadedStepParameter?.value ||
                    parameter?.sourceDisplayLabel !== loadedStepParameter?.sourceDisplayLabel
                ) {
                    ++unsavedDataCounter;
                    return true;
                }
            });
        });

        return unsavedDataCounter > 0;
    };

    /**
     * Trigger test integration action including validation and payment
     * mechanisms.
     * @returns {Promise<void>}
     */
    const testIntegration = async () => {
        let userConfirmedSave = false;
        const hasUnsavedChanges = hasUnsavedIntegrationData(),
            currentLocalIntegrationSteps = getIntegrationBuilderState('currentLocalIntegrationSteps');

        if (hasUnsavedChanges) {
            if (
                confirm(
                    'You have unsaved integration data that needs to be saved before testing. Would you like to save?',
                )
            ) {
                userConfirmedSave = true;
                await saveIntegrationBuilderData();
            }
        }

        // Verify integration data state
        const canRunTest =
            (!hasUnsavedChanges || userConfirmedSave) &&
            currentLocalIntegrationSteps &&
            currentLocalIntegrationSteps.length > 0 &&
            integrationId;

        if (canRunTest) {
            await executeTestIntegration();
        } else {
            return;
        }
    };

    /**
     * Set integration execute mode
     */
    const setIntegrationExecuteMode = () => {
        setGlobalDataRefItem('integrationExecuteMode', 'execute');
    };

    /**
     * Execute an integration test for the current integration.
     * @returns {Promise<void>}
     */
    const executeTestIntegration = async () => {
        const isExecuteMode = getGlobalDataRefItem('integrationExecuteMode') === 'execute';

        // Trigger the execution in the background, then surface a result dialog (Close / View Log).
        // The log itself is viewed in the UI's execution log viewer (opened in a new tab), so Connect
        // no longer streams the log inline.
        return testExecuteIntegration({
            integrationId,
            isExecuteMode,
            async: true,
        })
            .then((data) => {
                // The backend may return the executionId flat (data.executionId) or nested
                // (data.data.executionId); accept both, matching IntegrationApiCallTriggerTestModal,
                // so a nested response still opens the "Execution Started" modal instead of erroring.
                const executionId = data && data.executionId ? data.executionId : data?.data?.executionId || null;

                if (executionId) {
                    showSnackBarSuccessNotification('Integration execution started.');
                    setIntegrationBuilderStates({
                        integrationExecutionStartedModal: { open: true, executionId },
                    });
                } else {
                    showSnackBarErrorNotification('An error occurred starting this integration execution.');
                }
            })
            .catch((e) => {
                showSnackBarErrorNotification(e);
            })
            .finally(() => {
                isExecuteMode && removeGlobalDataRefItem('integrationExecuteMode');
            });
    };

    /**
     * Alias of the {@see executeManualIntegration()} function
     */
    const handleTestIntegration = async () => {
        await executeManualIntegration();
    };

    /**
     * Get the transform component zoom styles
     * @param {Object} transformComponent The transform component state.
     * @return {Object} The transform component zoom styles.
     */
    const getTransformComponentZoomStyles = (transformComponent) => {
        const { scale, stepItemWidth } = transformComponent,
            borderBottom = `${1 * scale}px solid #828282`;

        return {
            scale,
            stepItemWidth,
            root: {
                width: stepItemWidth * scale,
                maxWidth: transformComponentElementRef.current?.clientWidth || '100%',
                height: 88.5 * scale,
                transition: 'width 100ms, height 100ms',
                minWidth: '132px',
                minHeight: '56px',
            },
            rootBorder: {
                border: `${2 * scale} solid #6198ff`,
            },
            header: {
                borderBottom,
                marginBottom: `${scale > 1 ? 5 : 1}px`,
                transition: 'all 300ms',
            },
            borderBottom: {
                borderBottom,
                transition: 'all 300ms',
            },
            title: {
                fontSize: `${0.75 * scale}rem !important`,
                transition: 'all 300ms',
            },
            description: {
                fontSize: `${13 * scale}px !important`,
                transition: 'all 300ms',
            },
            fontSize: {
                fontSize: `${0.563 * scale}rem !important`,
                transition: 'all 300ms',
            },
            icon: {
                fontSize: `${1 * scale}rem !important`,
                transition: 'all 300ms',
            },
            plusIcon: {
                fontSize: `${1.2 * scale}rem !important`,
                transition: 'all 300ms',
            },
            circle: {
                fontSize: `${0.7 * scale}rem !important`,
                transition: 'all 300ms',
                marginLeft: '3px',
            },
            stepLine: {
                width: 3 * scale,
                height: `${7 * scale}vh !important`,
                maxHeight: '20vh',
                transition: 'all 300ms',
                marginLeft: `${4 * scale}px`,
                borderLeft: `${2 * scale}px dashed #6198ff !important`,
            },
        };
    };

    /**
     * Get step label
     * @param {String} stepType
     * @return {String} Step label
     */
    const getStepLabel = (stepType) => enabledStepTypes.find((step) => step.id === stepType)?.name || '';

    const inputMappingsDefaultState = [
        // {
        //     inputId: null,
        //     sourceStepId: null,
        //     sourceDataTypeId: null,
        //     sourceInputPath: [],
        //     sourceInputDelimiter: null,
        // },
    ];

    /**
     * Open parameter step modal
     * @param {Object} args
     */
    const handleOpenFieldSelectorModal = ({ isRootSelectable = true, onSubmit, ...props } = {}) => {
        setIntegrationBuilderStates({
            selectFieldModalState: {
                open: true,
                isRootSelectable,
                onSubmit,
                ...props,
            },
        });
    };

    /**
     * Open integration variable modal
     * @param {Object} args
     */
    const handleOpenIntegrationVariableModal = ({ variable, stepAction }) => {
        setIntegrationBuilderStates({
            integrationVariableModalState: {
                open: true,
                variable,
                stepAction,
            },
        });
    };

    /**
     * Open integration string input modal
     * @param {Object} args
     */
    const handleOpenIntegrationStringInputModal = ({
        stringValue,
        onSubmit,
        showOptionsForStringType = false,
        stringType = '',
        stringTypeOptions = [],
        customValidation,
        tooltip = null,
        showTreatAsTypeSelect = false,
        treatAsTypeValue = null,
    }) => {
        setIntegrationBuilderStates({
            integrationStringInputModalState: {
                open: true,
                stringValue,
                onSubmit,
                showOptionsForStringType,
                stringType,
                stringTypeOptions,
                customValidation,
                tooltip,
                showTreatAsTypeSelect,
                treatAsTypeValue,
            },
        });
    };

    /**
     * Open integration counter modal
     * @param {Object} args
     */
    const handleOpenIntegrationCounterModal = ({ counter, stepAction }) => {
        setIntegrationBuilderStates({
            integrationCounterModalState: {
                open: true,
                counter,
                stepAction,
            },
        });
    };

    /**
     * Handle integration variable changes
     * @param {Object} variable
     * @param {String} variable.name
     * @param {String} variable.value
     * @param {Boolean} variable.isPersistent
     * @param {String} actionType (add|edit)
     * @param {Number} index
     * @param {Boolean} hasChangedNameOnEdit (true|false) - true if the variable name has changed from old name on edit
     */
    const handleIntegrationVariableChanges = ({ variable, actionType, index, hasChangedNameOnEdit }) => {
        if (constants.RESERVED_VARIABLE_NAMES.includes(variable.name)) {
            showSnackBarErrorNotification(`"${variable.name}" is a reserved name. Please use a different name.`);
            return {
                isError: true,
                errors: { name: 'Variable name is reserved.' },
            };
        }

        const variables = selectedIntegrationData?.variables || [],
            counters = selectedIntegrationData?.counters || [],
            variableNameExists = variables.find((v) => v.name === variable.name) !== undefined,
            variableNameExistsInCounters =
                counters.find((v) => v.name === variable.name) !== undefined ||
                constants.RESERVED_COUNTER_NAMES.includes(variable.name);

        if (actionType === 'add') {
            if (variableNameExists) {
                showSnackBarErrorNotification('Variable name already exists.');
                return {
                    isError: true,
                    errors: {
                        name: 'Variable name already exists.',
                    },
                };
            } else if (variableNameExistsInCounters) {
                showSnackBarErrorNotification('Variable name already exists in counters.');
                return {
                    isError: true,
                    errors: {
                        name: 'Name already exists in counters.',
                    },
                };
            }
        } else if (actionType === 'edit') {
            if (hasChangedNameOnEdit && variableNameExists) {
                showSnackBarErrorNotification('Variable name already exists.');
                return {
                    isError: true,
                    errors: {
                        name: 'Variable name already exists.',
                    },
                };
            } else if (hasChangedNameOnEdit && variableNameExistsInCounters) {
                showSnackBarErrorNotification('Variable name already exists in counters.');
                return {
                    isError: true,
                    errors: {
                        name: 'Name already exists in counters.',
                    },
                };
            }
        }

        const newVariables = [...variables];

        if (actionType === 'edit') {
            newVariables[index] = variable;
        } else if (actionType === 'add') {
            newVariables.push(variable);
        } else if (actionType === 'delete') {
            newVariables.splice(index, 1);
        }

        setSelectedIntegrationData({
            ...selectedIntegrationData,
            variables: newVariables,
        });
        setUnsavedDataState();

        const action = actionType === 'edit' ? 'edited' : actionType === 'delete' ? 'deleted' : 'added';
        showSnackBarSuccessNotification(`Variable ${action} successfully.`);

        return {
            isError: false,
        };
    };

    /**
     * Handle integration counter changes
     * @param {Object} args
     * @param {Object} args.counter
     * @param {String} args.counter.name
     * @param {String} args.counter.value
     * @param {Boolean} args.counter.isPersistent
     * @param {String} args.actionType (add|edit)
     * @param {Number} args.index
     * @param {Boolean} args.hasChangedNameOnEdit (true|false) - true if the counter name has changed from old name on edit
     */

    const handleIntegrationCounterChanges = ({ counter, actionType, index, hasChangedNameOnEdit }) => {
        if (constants.RESERVED_COUNTER_NAMES.includes(counter.name)) {
            showSnackBarErrorNotification(`"${counter.name}" is a reserved name. Please use a different name.`);
            return {
                isError: true,
                errors: { name: 'Counter name is reserved.' },
            };
        }

        const counters = selectedIntegrationData?.counters || [],
            variables = selectedIntegrationData?.variables || [],
            counterNameExistsInCounters = counters.find((v) => v.name === counter.name) !== undefined,
            counterNameExistsInVariables =
                variables.find((v) => v.name === counter.name) !== undefined ||
                constants.RESERVED_VARIABLE_NAMES.includes(counter.name);

        if (actionType === 'add') {
            if (counterNameExistsInCounters) {
                showSnackBarErrorNotification('Counter name already exists.');
                return {
                    isError: true,
                    errors: {
                        name: 'Counter name already exists.',
                    },
                };
            } else if (counterNameExistsInVariables) {
                showSnackBarErrorNotification('Counter name already exists in variables.');
                return {
                    isError: true,
                    errors: {
                        name: 'Counter name already exists in variables.',
                    },
                };
            }
        } else if (actionType === 'edit' && hasChangedNameOnEdit) {
            if (counterNameExistsInCounters) {
                showSnackBarErrorNotification('Counter name already exists.');
                return {
                    isError: true,
                    errors: {
                        name: 'Counter name already exists.',
                    },
                };
            } else if (counterNameExistsInVariables) {
                showSnackBarErrorNotification('Counter name already exists in variables.');
                return {
                    isError: true,
                    errors: {
                        name: 'Counter name already exists in variables.',
                    },
                };
            }
        }

        const newCounters = [...counters];

        if (actionType === 'edit') {
            newCounters[index] = counter;
        } else if (actionType === 'add') {
            newCounters.push(counter);
        } else if (actionType === 'delete') {
            newCounters.splice(index, 1);
        }

        setSelectedIntegrationData({
            ...selectedIntegrationData,
            counters: newCounters,
        });
        setUnsavedDataState();

        const action = actionType === 'edit' ? 'edited' : actionType === 'delete' ? 'deleted' : 'added';
        showSnackBarSuccessNotification(`Counter ${action} successfully.`);

        return {
            isError: false,
        };
    };

    const handleNavigateToDataTypeScreen = () => {
        if (!isIntegrationEditable) return;

        const locationHost = window.location.host;
        if (locationHost?.indexOf('intely') > -1) {
            setMyDataTypeCreateDataTypeModalState({
                open: true,
                actionType: 'create',
                isIntegrationStepApiTrigger: true,
            });
        }
    };

    /**
     * Pre-loaded props
     */
    return {
        classes,
        zoomInRef,
        stepTypes,
        zoomOutRef,
        isIntegrationEditable,
        isDataLoaded,
        drawerStyles,
        nextStepIdRef,
        indexCounterRef,
        enabledStepTypes,
        defaultStepParam,
        highestStepIdRef,
        stepsCacheListRef,
        parameterDataRegex,
        stepCounterListRef,
        triggerDropdownList,
        lastDeletedStepIdRef,
        selectedIntegrationData,
        saveIntegrationButtonRef,
        integrationControlOptions,
        inputMappingsDefaultState,
        droppedStepActionCounterRef,
        transformComponentElementRef,
        stepReferencedInOtherStepsRef,

        /**
         * Functions
         */
        setIntegrationExecuteMode,
        getStepLabel,
        setUnsavedDataState,
        setupStepDefaultData,
        handleTestIntegration,
        executeTestIntegration,
        executeManualIntegration,
        validateIntegrationSteps,
        hasUnsavedIntegrationData,
        hasUnsavedIntegrationMetaData,
        addIntegrationBuilderStep,
        saveIntegrationBuilderData,
        updateLocalIntegrationSteps,
        handleOpenFieldSelectorModal,
        removeIntegrationBuilderStep,
        addNewIntegrationBuilderStep,
        handleNavigateToDataTypeScreen,
        getTransformComponentZoomStyles,
        handleOpenIntegrationVariableModal,
        handleIntegrationVariableChanges,
        handleOpenIntegrationStringInputModal,
        handleOpenIntegrationCounterModal,
        handleIntegrationCounterChanges,
    };
};

/**
 * Update the trigger data type ID when data type is created from integration builder
 * @param {String} dataTypeId
 */
export const updateIntegrationBuilderTriggerDataTypeId = (newDataTypeId) => {
    const currentLocalIntegrationSteps = getIntegrationBuilderState('currentLocalIntegrationSteps'),
        steps = [...currentLocalIntegrationSteps],
        firstStepData = { ...steps[0] };

    steps[0] = {};
    steps[0] = {
        ...firstStepData,
        triggerDataTypeId: newDataTypeId,
    };

    setIntegrationBuilderStates({
        currentLocalIntegrationSteps: steps,
    });
};

/**
 * Set the created mapping
 * @param {Object} mapping
 */
export const setIntegrationStepCreatedMapping = (mapping = {}) => {
    setIntegrationBuilderStates({
        integrationStepCreatedMapping: mapping,
    });
};

/**
 * TODO refactor name, should be tied in with field selector modal since it's used there exclusively
 * Update integration  step object
 * @param {Object} args
 */
export const updateIntegrationStepObject = ({
    inputPath,
    stepMode,
    inputType,
    dataTypeId,
    inputString,
    currentStepId,
    expressionField,
    comparisonInputId,
    // todo refactor, something like isTargetStepLoopAtRootField
    isIterateLoopStep,
    setUnsavedDataState,
    expressionCompareToField,
    updateLocalIntegrationSteps,
    caseConditionalStepFieldValue,
    comparisonInputIndex = 0,
    filters = [],
    crosswalkId = null,
    stepFieldValueType: retrieveValueType,
    stepReferencedInOtherStepsRef,
    variableIndex,
    fieldKey,
    treatAsType,
    // variable,
    isIntegrationEditable,
    inputIndexes,
    // additional data is used to pass extra fields through for saving
    additionalData = {},
} = {}) => {
    if (!isIntegrationEditable) return;

    if (!stepMode) {
        stepMode = constants.STEP_MODE_NORMAL;
    }

    let targetStep = null;
    const { selectedStepMetadata, selectFieldModalState } = getIntegrationBuilderStates(),
        {
            inputMappingId,
            inputMappingIndex,
            conditionalStepIndex,
            parameterIndex,
            conditionalExpressionIndexes,
            caseConditionalStepField,
            restfulRequestStepQueryParamIndex,
            restfulRequestStepQueryParamObjectProp,
            skipAddingNewParameterToList,
        } = selectFieldModalState || {};

    const currentStepData = selectedStepMetadata.stepData,
        requestBodyValueType = currentStepData?.requestBodyValueType,
        isStepOutputSelected = requestBodyValueType === constants.STEP_REQUEST_BODY_VALUE_TYPE_STEP_OUTPUT,
        isMappingSelected = requestBodyValueType === constants.STEP_REQUEST_BODY_VALUE_TYPE_MAPPING,
        isConvertStepType = currentStepData?.stepType === constants.STEP_TYPE_CONVERT,
        isConditionalStepType = currentStepData?.stepType === constants.STEP_TYPE_CONDITIONAL,
        isSetVariableStepType = currentStepData?.stepType === constants.STEP_TYPE_SET_VARIABLE,
        isComparisonStepType = currentStepData?.stepType === constants.STEP_TYPE_COMPARISON,
        isRunIntegrationStepType = currentStepData?.stepType === constants.STEP_TYPE_RUN_INTEGRATION,
        isCrosswalkStepType = currentStepData?.stepType === constants.STEP_TYPE_CROSSWALK,
        isDataProcessingStepType = currentStepData?.stepType === constants.STEP_TYPE_DATA_PROCESSOR,
        isCastStepType = currentStepData?.stepType === constants.STEP_TYPE_CAST;

    // Set the step reference
    stepReferencedInOtherStepsRef.current[currentStepData?.stepId] = { [currentStepId]: 1 };

    const { steps, topLevelLoopStepData } = getMutableIntegrationStepDeep({
        stepId: selectedStepMetadata.stepId,
        topLevelLoopStepData: selectedStepMetadata.topLevelLoopStepData,
        callback: ({ step }) => {
            const newInputPath = inputPath || [],
                newDataTypeId = dataTypeId,
                newFilters = filters || [],
                newRetrieveValueType = retrieveValueType || constants.STEP_FIELD_SELECT_RETRIEVE_VALUE_TYPE_VALUE,
                newTreatAsType = treatAsType || null;

            // Check if the source step is a Transmute step
            let newSourceDisplayLabel = isIterateLoopStep ? 'Step ' + currentStepId : inputString;
            const currentLocalIntegrationSteps = getIntegrationBuilderState('currentLocalIntegrationSteps');

            // Recursive function to find step by ID in nested steps
            const findStepById = (steps, stepId) => {
                for (const step of steps) {
                    if (step.stepId === stepId) {
                        return step;
                    }
                    // Check in containedSteps for loop/conditional steps
                    if (step.containedSteps && step.containedSteps.length > 0) {
                        const found = findStepById(step.containedSteps, stepId);
                        if (found) return found;
                    }
                }
                return null;
            };

            const sourceStep = findStepById(currentLocalIntegrationSteps, currentStepId);
            if (sourceStep && sourceStep.stepType === constants.STEP_TYPE_TRANSMUTE) {
                // Add "Transmuted" prefix to the display label
                newSourceDisplayLabel = newSourceDisplayLabel.includes(':')
                    ? newSourceDisplayLabel.replace(':', ': Transmuted')
                    : `Transmuted ${newSourceDisplayLabel}`;
            }

            if (stepMode === constants.STEP_MODE_ADVANCED) {
                if (!step?.advancedOptions) {
                    step.advancedOptions = {};
                }

                switch (inputType) {
                    case constants.STEP_FIELD_SELECT_INPUT_TYPE_ADVANCED_SET_FIELD:
                        {
                            if (!step.advancedOptions?.fields) {
                                step.advancedOptions.fields = [];
                            }

                            const remainingItems = step.advancedOptions.fields.filter((item) => item.key !== fieldKey);

                            remainingItems.push({
                                key: fieldKey,
                                value: {
                                    sourceStepId: currentStepId,
                                    sourceDataTypeId: newDataTypeId,
                                    sourceDisplayLabel: newSourceDisplayLabel,
                                    sourceRetrieveValueType: newRetrieveValueType,
                                    sourceInputPath: newInputPath,
                                    sourceInputFilters: newFilters,
                                    crosswalkId: crosswalkId,
                                },
                            });

                            step.advancedOptions.fields = remainingItems;
                        }
                        break;
                    case constants.STEP_FIELD_SELECT_INPUT_TYPE_ADVANCED_SET_OPTION:
                        step.advancedOptions[fieldKey] = {
                            sourceStepId: currentStepId,
                            sourceDataTypeId: newDataTypeId,
                            sourceDisplayLabel: newSourceDisplayLabel,
                            sourceRetrieveValueType: newRetrieveValueType,
                            sourceInputPath: newInputPath,
                            sourceInputFilters: newFilters,
                            crosswalkId: crosswalkId,
                        };
                        break;
                }
            } else {
                // Update step parameter
                if (inputType === 'stepParameter') {
                    if (!currentStepData?.parameters) {
                        step.parameters = [];
                    }

                    const parameters = step.parameters,
                        selectedParameter = parameters[parameterIndex] || {};

                    parameters[parameterIndex] = {
                        ...selectedParameter,
                        value: '',
                        sourceStepId: currentStepId,
                        sourceDataTypeId: newDataTypeId,
                        sourceDisplayLabel: newSourceDisplayLabel,
                        sourceRetrieveValueType: newRetrieveValueType,
                        sourceDataTypeInputPath: newInputPath,
                        sourceDataTypeInputPathFilters: newFilters,
                        crosswalkId: crosswalkId,
                        treatAsType: newTreatAsType,
                    };

                    parameters[parameterIndex] = normalizeAppRequestStepParameter(parameters[parameterIndex]);

                    if (!skipAddingNewParameterToList) {
                        const lastParameter = parameters[parameters.length - 1];
                        if (lastParameter.name && (lastParameter.value || lastParameter?.sourceDisplayLabel)) {
                            parameters.push({ name: '', value: '' });
                        }
                    }

                    step.parameters = parameters;
                } else if (isConditionalStepType) {
                    // Conditional step
                    if (
                        caseConditionalStepFieldValue ||
                        currentStepData?.conditionalType === constants.CONDITIONAL_TYPE_IF
                    ) {
                        // If condition
                        filterConditionalStepExpression({
                            step,
                            conditionIndex: conditionalStepIndex,
                            expressionIndexes: [...conditionalExpressionIndexes],
                            callback: ({ expression }) => {
                                if (expressionField) {
                                    expression.leftOperandValue = '';
                                    expression.leftOperandSourceStepId = currentStepId;
                                    expression.leftOperandSourceInputPath = newInputPath;
                                    expression.leftOperandSourceDataTypeId = newDataTypeId;
                                    expression.leftOperandSourceRetrieveValueType = newRetrieveValueType;
                                    expression.leftOperandSourceDataTypeDisplayLabel = newSourceDisplayLabel;
                                    expression.leftOperandSourceFilters = newFilters;
                                    expression.leftOperandCrosswalkId = crosswalkId;
                                    expression.leftOperandTreatAsType = newTreatAsType;
                                } else if (expressionCompareToField || caseConditionalStepFieldValue) {
                                    expression.rightOperandValue = '';
                                    expression.rightOperandSourceStepId = currentStepId;
                                    expression.rightOperandSourceInputPath = newInputPath;
                                    expression.rightOperandSourceDataTypeId = newDataTypeId;
                                    expression.rightOperandSourceRetrieveValueType = newRetrieveValueType;
                                    expression.rightOperandSourceDataTypeDisplayLabel = newSourceDisplayLabel;
                                    expression.rightOperandSourceFilters = newFilters;
                                    expression.rightOperandCrosswalkId = crosswalkId;
                                    expression.rightOperandTreatAsType = newTreatAsType;
                                }
                            },
                        });
                    } else if (currentStepData?.conditionalType === constants.CONDITIONAL_TYPE_CASE) {
                        // Case condition
                        if (caseConditionalStepField) {
                            step.conditionSourceStepIdForComparison = currentStepId;
                            step.conditionSourceInputPathForComparison = newInputPath;
                            step.conditionSourceDataTypeIdForComparison = newDataTypeId;
                            step.conditionSourceDataTypeDisplayLabelForComparison = newSourceDisplayLabel;
                            step.conditionSourceFilters = newFilters;
                            step.conditionSourceRetrieveValueType = newRetrieveValueType;
                            step.conditionCrosswalkId = crosswalkId;
                            step.conditionTreatAsType = newTreatAsType;
                        }
                    }
                } else if (currentStepData?.stepType === constants.STEP_TYPE_LOOP) {
                    // Loop step
                    if (inputType === constants.STEP_FIELD_SELECT_INPUT_TYPE_LOOP_INTELY_STORAGE_FILE_PATH) {
                        step.loopInputIntelyFilePathSourceStepId = currentStepId;
                        step.loopInputIntelyFilePathDataTypeId = newDataTypeId;
                        step.loopInputIntelyFilePathInputPath = newInputPath;
                        step.loopInputIntelyFilePathDisplayLabel = newSourceDisplayLabel;
                        step.loopInputFilters = newFilters;
                        step.loopInputRetrieveValueType = newRetrieveValueType;
                        step.loopInputCrosswalkId = crosswalkId;
                        step.loopInputIntelyFilePath = null;
                    } else {
                        // conditionalExpressionIndexes will be set from the condition component so we can use that as a flag to modify while loop expressions
                        if (Array.isArray(conditionalExpressionIndexes) && conditionalExpressionIndexes.length > 0) {
                            filterConditionalStepExpression({
                                step,
                                conditionIndex: conditionalStepIndex,
                                expressionIndexes: [...conditionalExpressionIndexes],
                                callback: ({ expression }) => {
                                    if (expressionField) {
                                        expression.leftOperandValue = '';
                                        expression.leftOperandSourceStepId = currentStepId;
                                        expression.leftOperandSourceInputPath = newInputPath;
                                        expression.leftOperandSourceDataTypeId = newDataTypeId;
                                        expression.leftOperandSourceRetrieveValueType = newRetrieveValueType;
                                        expression.leftOperandSourceDataTypeDisplayLabel = newSourceDisplayLabel;
                                        expression.leftOperandSourceFilters = newFilters;
                                        expression.leftOperandCrosswalkId = crosswalkId;
                                    } else if (expressionCompareToField) {
                                        expression.rightOperandValue = '';
                                        expression.rightOperandSourceStepId = currentStepId;
                                        expression.rightOperandSourceInputPath = newInputPath;
                                        expression.rightOperandSourceDataTypeId = newDataTypeId;
                                        expression.rightOperandSourceRetrieveValueType = newRetrieveValueType;
                                        expression.rightOperandSourceDataTypeDisplayLabel = newSourceDisplayLabel;
                                        expression.rightOperandSourceFilters = newFilters;
                                        expression.rightOperandCrosswalkId = crosswalkId;
                                    }
                                },
                            });
                        } else {
                            step.sourceStepIdForLoop = currentStepId;
                            step.sourceInputPathForLoop = newInputPath;
                            step.sourceDataTypeIdForLoop = newDataTypeId;
                            step.sourceDataTypeDisplayLabelForLoop = newSourceDisplayLabel;
                            step.sourceFiltersForLoop = newFilters;
                            step.sourceRetrieveValueTypeForLoop = retrieveValueType;
                            step.crosswalkIdForLoop = crosswalkId;
                        }
                    }
                } else if (isStepOutputSelected) {
                    // Step output
                    step.requestBodySourceStepId = currentStepId;
                    step.requestBodyInputFilters = newFilters;
                    step.requestBodySourceInputPath = newInputPath;
                    step.requestBodySourceDataTypeId = newDataTypeId;
                    step.requestBodySourceDisplayLabel = newSourceDisplayLabel;
                    step.requestBodySourceRetrieveValueType = newRetrieveValueType;
                    step.requestBodyCrosswalkId = crosswalkId;
                } else if (isMappingSelected || isConvertStepType) {
                    // Input mapping or convert step
                    if (!step.inputMappings) {
                        step.inputMappings = [];
                    }

                    if (isMappingSelected || isIterateLoopStep) {
                        step.requestBodySourceStepId = currentStepId;
                    }

                    step.inputMappings[inputMappingIndex] = {
                        inputId: inputMappingId,
                        sourceStepId: currentStepId,
                        sourceDataTypeId: newDataTypeId,
                        sourceInputPath: newInputPath,
                        sourceInputFilters: newFilters,
                        sourceDisplayLabel: newSourceDisplayLabel,
                        sourceRetrieveValueType: newRetrieveValueType,
                        sourceInputDelimiter: step.inputMappings[inputMappingIndex]?.sourceInputDelimiter ?? undefined,
                        sourceInputEntryOffset:
                            step.inputMappings[inputMappingIndex]?.sourceInputEntryOffset ?? undefined,
                        crosswalkId: crosswalkId,
                        treatAsType: newTreatAsType,
                    };
                } else if (currentStepData?.stepType === constants.STEP_TYPE_RESTFUL_REQUEST) {
                    step[restfulRequestStepQueryParamObjectProp][restfulRequestStepQueryParamIndex] = {
                        ...(step[restfulRequestStepQueryParamObjectProp][restfulRequestStepQueryParamIndex] || {}),
                        value: '',
                        inputId: inputMappingId,
                        sourceStepId: currentStepId,
                        sourceDataTypeId: newDataTypeId,
                        sourceInputPath: newInputPath,
                        sourceInputFilters: newFilters,
                        sourceDisplayLabel: newSourceDisplayLabel,
                        sourceRetrieveValueType: newRetrieveValueType,
                        crosswalkId: crosswalkId,
                    };

                    delete step[restfulRequestStepQueryParamObjectProp][restfulRequestStepQueryParamIndex].value;

                    if ('restfulRequestStepBody' === restfulRequestStepQueryParamObjectProp) {
                        delete step.restfulRequestStepBodyRawValue;
                    }
                } else if (currentStepData?.stepType === constants.STEP_TYPE_EXECUTE_SCRIPT) {
                    if (!Array.isArray(step.executeScriptParameters)) {
                        step.executeScriptParameters = [];
                    }

                    if (!step.executeScriptParameters[parameterIndex]) {
                        step.executeScriptParameters[parameterIndex] = {};
                    }

                    step.executeScriptParameters[parameterIndex] = {
                        ...step.executeScriptParameters[parameterIndex],
                        sourceStepId: currentStepId,
                        sourceDataTypeId: newDataTypeId,
                        sourceInputPath: newInputPath,
                        sourceInputFilters: newFilters,
                        sourceDisplayLabel: newSourceDisplayLabel,
                        sourceRetrieveValueType: newRetrieveValueType,
                        crosswalkId: crosswalkId,
                        treatAsType: newTreatAsType,
                    };

                    if (step.executeScriptParameters[parameterIndex]?.rawData) {
                        delete step.executeScriptParameters[parameterIndex].rawData;
                    }
                } else if (isSetVariableStepType) {
                    // Set Variable step
                    const variable = currentStepData.variables[variableIndex] || {};
                    const variableUpdates = {
                        ...variable,
                        value: null,
                        sourceStepId: currentStepId,
                        sourceDataTypeId: newDataTypeId,
                        sourceDisplayLabel: newSourceDisplayLabel,
                        sourceInputPath: newInputPath,
                        sourceInputFilters: newFilters,
                        sourceRetrieveValueType: newRetrieveValueType,
                        crosswalkId: crosswalkId,
                        treatAsType: newTreatAsType,
                    };

                    if (step.variables) {
                        step.variables[variableIndex] = variableUpdates;
                    } else {
                        step.variables = [variableUpdates];
                    }
                } else if (isComparisonStepType) {
                    switch (inputType) {
                        case constants.STEP_FIELD_SELECT_INPUT_TYPE_COMPARISON_INPUT:
                            if (comparisonInputIndex || comparisonInputIndex === 0) {
                                if (!step.comparisonInputs) {
                                    step.comparisonInputs = [];
                                }

                                step.comparisonInputs[comparisonInputIndex] = {
                                    inputId: comparisonInputId,
                                    sourceStepId: currentStepId,
                                    sourceDataTypeId: newDataTypeId,
                                    sourceInputPath: newInputPath,
                                    sourceInputFilters: newFilters,
                                    sourceDisplayLabel: newSourceDisplayLabel,
                                    sourceRetrieveValueType: newRetrieveValueType,
                                    sourceInputDelimiter:
                                        step.comparisonInputs[comparisonInputIndex]?.sourceInputDelimiter ?? undefined,
                                    crosswalkId: crosswalkId,
                                };
                            }
                            break;
                        case constants.STEP_FIELD_SELECT_INPUT_TYPE_COMPARISON_FILE_DESTINATION:
                            step.comparisonFileDestinationReference = {
                                sourceStepId: currentStepId,
                                sourceDataTypeId: newDataTypeId,
                                sourceInputPath: newInputPath,
                                sourceDisplayLabel: newSourceDisplayLabel,
                                sourceInputFilter: newFilters,
                                sourceRetrieveValueType: newRetrieveValueType,
                                crosswalkId: crosswalkId,
                            };
                            delete step['comparisonFileDestination'];
                            break;
                    }
                } else if (isCrosswalkStepType) {
                    if (!step?.crosswalkData) {
                        step.crosswalkData = {};
                    }
                    if (!Array.isArray(step.crosswalkData?.valuesToCrosswalk)) {
                        step.crosswalkData.valuesToCrosswalk = [];
                    }

                    step.crosswalkData.valuesToCrosswalk[parameterIndex] = {
                        sourceStepId: currentStepId,
                        sourceDataTypeId: newDataTypeId,
                        sourceInputPath: newInputPath,
                        sourceInputFilters: newFilters,
                        sourceDisplayLabel: newSourceDisplayLabel,
                        sourceRetrieveValueType: newRetrieveValueType,
                        crosswalkId: crosswalkId,
                        treatAsType: newTreatAsType,
                        rawData: null,
                    };
                } else if (isRunIntegrationStepType) {
                    switch (step.integrationDataValueType) {
                        case constants.STEP_REQUEST_BODY_VALUE_TYPE_MAPPING:
                            if (!step.inputMappings) {
                                step.inputMappings = [];
                            }

                            step.integrationDataSourceStepId = currentStepId;

                            step.inputMappings[comparisonInputIndex] = {
                                inputId: inputMappingId,
                                sourceStepId: currentStepId,
                                sourceDataTypeId: newDataTypeId,
                                sourceInputPath: newInputPath,
                                sourceInputFilters: newFilters,
                                sourceDisplayLabel: newSourceDisplayLabel,
                                sourceRetrieveValueType: newRetrieveValueType,
                                crosswalkId: crosswalkId,
                            };
                            break;
                        case constants.STEP_REQUEST_BODY_VALUE_TYPE_STEP_OUTPUT:
                            step.integrationDataSourceStepId = currentStepId;
                            step.integrationDataSourceDisplayLabel = newSourceDisplayLabel;
                            step.integrationDataTypeId = newDataTypeId;
                            step.integrationDataSourceDataTypeInputPath = newInputPath;
                            step.integrationDataFilters = newFilters;
                            step.integrationDataRetrieveValueType = newRetrieveValueType;
                            step.integrationDataCrosswalkId = crosswalkId;
                            break;
                    }
                } else if (isDataProcessingStepType) {
                    switch (inputType) {
                        case constants.STEP_FIELD_SELECT_INPUT_TYPE_DATA_PROCESSOR_ARGUMENT_DATA_TYPE:
                            if (inputIndexes.argumentIndex || inputIndexes.argumentIndex === 0) {
                                if (!step.dataProcessorOptions) {
                                    step.dataProcessorOptions = {};
                                }

                                if (!step.dataProcessorOptions.executionData) {
                                    step.dataProcessorOptions.executionData = {};
                                }

                                if (!step.dataProcessorOptions['executionData']['arguments']) {
                                    step.dataProcessorOptions['executionData']['arguments'] = [];
                                }

                                step.dataProcessorOptions['executionData']['arguments'][inputIndexes.argumentIndex] = {
                                    argumentId: additionalData.argumentId,
                                    sourceStepId: currentStepId,
                                    sourceDataTypeId: newDataTypeId,
                                    sourceInputPath: newInputPath,
                                    sourceInputFilters: newFilters,
                                    sourceDisplayLabel: newSourceDisplayLabel,
                                    sourceRetrieveValueType: newRetrieveValueType,
                                    crosswalkId: crosswalkId,
                                };
                            }
                            break;
                        case constants.STEP_FIELD_SELECT_INPUT_TYPE_DATA_PROCESSOR_INPUT_FILE_DATA_TYPE:
                            if (!step.dataProcessorOptions) {
                                step.dataProcessorOptions = {};
                            }

                            if (!step.dataProcessorOptions.executionData) {
                                step.dataProcessorOptions.executionData = {};
                            }

                            if (!step.dataProcessorOptions['executionData']['intelyStorageInputFiles']) {
                                step.dataProcessorOptions['executionData']['intelyStorageInputFiles'] = [];
                            }

                            step.dataProcessorOptions['executionData']['intelyStorageInputFiles'][
                                inputIndexes.inputFileIndex
                            ] = {
                                jobInputFileId: additionalData.jobInputFileId,
                                intelyStorageFile: {
                                    sourceStepId: currentStepId,
                                    sourceDataTypeId: newDataTypeId,
                                    sourceInputPath: newInputPath,
                                    sourceInputFilters: newFilters,
                                    sourceDisplayLabel: newSourceDisplayLabel,
                                    sourceRetrieveValueType: newRetrieveValueType,
                                    crosswalkId: crosswalkId,
                                },
                            };
                            break;
                    }
                } else if (isCastStepType) {
                    switch (inputType) {
                        case constants.STEP_FIELD_SELECT_INPUT_TYPE_CAST_VALUE_TYPE:
                            if (!step.castOptions) {
                                step.castOptions = /** @type {CastOptions} */ {};
                            }

                            step.castOptions.sourceStepId = currentStepId;
                            step.castOptions.sourceDataTypeId = newDataTypeId;
                            step.castOptions.sourceInputPath = newInputPath;
                            step.castOptions.sourceInputFilters = newFilters;
                            step.castOptions.sourceDisplayLabel = newSourceDisplayLabel;
                            step.castOptions.sourceRetrieveValueType = newRetrieveValueType;
                            step.castOptions.crosswalkId = crosswalkId;
                            step.castOptions.rawData = null;
                            break;
                    }
                } else if (step.stepType === constants.STEP_TYPE_TRANSMUTE) {
                    if (!step.transmuteOptions) {
                        step.transmuteOptions = {
                            expressions: [],
                            fieldsToModify: [],
                        };
                    }

                    switch (inputType) {
                        case constants.STEP_FIELD_SELECT_INPUT_TYPE_TRANSMUTE_FIELD_TO_REPLACE: {
                            if (!step.transmuteOptions.fieldsToModify) {
                                step.transmuteOptions.fieldsToModify = [];
                            }

                            const fieldIndex = inputIndexes?.fieldIndex;
                            if (fieldIndex !== undefined) {
                                if (!step.transmuteOptions.fieldsToModify[fieldIndex]) {
                                    step.transmuteOptions.fieldsToModify[fieldIndex] = {
                                        inputPathToReplace: [],
                                        replaceWith: {},
                                    };
                                }
                                step.transmuteOptions.fieldsToModify[fieldIndex].inputPathToReplace = newInputPath;
                                step.transmuteOptions.fieldsToModify[fieldIndex].displayLabelToReplace =
                                    newSourceDisplayLabel;

                                // Capture dataTypeId from modal when first field is selected
                                if (!step.transmuteOptions.dataTypeId && newDataTypeId) {
                                    step.transmuteOptions.dataTypeId = newDataTypeId;
                                }
                            }
                            break;
                        }

                        case constants.STEP_FIELD_SELECT_INPUT_TYPE_TRANSMUTE_REPLACE_WITH: {
                            if (!step.transmuteOptions.fieldsToModify) {
                                step.transmuteOptions.fieldsToModify = [];
                            }

                            const replaceFieldIndex = inputIndexes?.fieldIndex;
                            if (replaceFieldIndex !== undefined) {
                                if (!step.transmuteOptions.fieldsToModify[replaceFieldIndex]) {
                                    step.transmuteOptions.fieldsToModify[replaceFieldIndex] = {
                                        inputPathToReplace: [],
                                        replaceWith: {},
                                    };
                                }

                                const replaceWith =
                                    step.transmuteOptions.fieldsToModify[replaceFieldIndex].replaceWith || {};

                                // Check if a field was selected (has source field data) or is loop iteration data
                                if ((newInputPath && newInputPath.length > 0) || isIterateLoopStep) {
                                    // Field selection - save source properties
                                    replaceWith.sourceStepId = currentStepId;
                                    replaceWith.sourceDataTypeId = newDataTypeId;
                                    replaceWith.sourceInputPath = newInputPath;
                                    replaceWith.sourceDisplayLabel = newSourceDisplayLabel;
                                    replaceWith.sourceInputFilters = newFilters;
                                    replaceWith.sourceRetrieveValueType = newRetrieveValueType;
                                    replaceWith.crosswalkId = crosswalkId;
                                    replaceWith.rawData = undefined;
                                } else if (inputString) {
                                    // Raw text input - save as rawData
                                    replaceWith.rawData = inputString;
                                    replaceWith.sourceStepId = undefined;
                                    replaceWith.sourceDataTypeId = undefined;
                                    replaceWith.sourceInputPath = undefined;
                                    replaceWith.sourceDisplayLabel = undefined;
                                    replaceWith.sourceInputFilters = undefined;
                                    replaceWith.sourceRetrieveValueType = undefined;
                                    replaceWith.crosswalkId = undefined;
                                }

                                replaceWith.treatAsType = treatAsType;
                                step.transmuteOptions.fieldsToModify[replaceFieldIndex].replaceWith = replaceWith;
                            }
                            break;
                        }

                        case constants.STEP_FIELD_SELECT_INPUT_TYPE_EXPRESSION_GROUP_FIELD_LEFT:
                        case constants.STEP_FIELD_SELECT_INPUT_TYPE_EXPRESSION_GROUP_FIELD_RIGHT: {
                            // Handle transmute step condition expression fields
                            if (
                                Array.isArray(conditionalExpressionIndexes) &&
                                conditionalExpressionIndexes.length > 0
                            ) {
                                filterConditionalStepExpression({
                                    step,
                                    conditionIndex: conditionalStepIndex,
                                    expressionIndexes: [...conditionalExpressionIndexes],
                                    callback: ({ expression }) => {
                                        if (
                                            inputType ===
                                            constants.STEP_FIELD_SELECT_INPUT_TYPE_EXPRESSION_GROUP_FIELD_LEFT
                                        ) {
                                            expression.leftOperandValue = '';
                                            expression.leftOperandSourceStepId = currentStepId;
                                            expression.leftOperandSourceInputPath = newInputPath;
                                            expression.leftOperandSourceDataTypeId = newDataTypeId;
                                            expression.leftOperandSourceRetrieveValueType = newRetrieveValueType;
                                            expression.leftOperandSourceDataTypeDisplayLabel = newSourceDisplayLabel;
                                            expression.leftOperandSourceFilters = newFilters;
                                            expression.leftOperandCrosswalkId = crosswalkId;
                                            expression.leftOperandTreatAsType = newTreatAsType;
                                        } else if (
                                            inputType ===
                                            constants.STEP_FIELD_SELECT_INPUT_TYPE_EXPRESSION_GROUP_FIELD_RIGHT
                                        ) {
                                            expression.rightOperandValue = '';
                                            expression.rightOperandSourceStepId = currentStepId;
                                            expression.rightOperandSourceInputPath = newInputPath;
                                            expression.rightOperandSourceDataTypeId = newDataTypeId;
                                            expression.rightOperandSourceRetrieveValueType = newRetrieveValueType;
                                            expression.rightOperandSourceDataTypeDisplayLabel = newSourceDisplayLabel;
                                            expression.rightOperandSourceFilters = newFilters;
                                            expression.rightOperandCrosswalkId = crosswalkId;
                                            expression.rightOperandTreatAsType = newTreatAsType;
                                        }
                                    },
                                });
                            }
                            break;
                        }
                    }
                }
            }

            targetStep = step;
        },
    });

    if (targetStep) {
        const states = {
            selectedStepMetadata: {
                ...selectedStepMetadata,
                topLevelLoopStepData,
                stepData: targetStep,
            },
        };

        if (isConditionalStepType) {
            states.conditionalStepValidation = clearConditionalIndexValidationError({
                stepId: targetStep.stepId,
                conditionIndex: conditionalStepIndex,
            });
        }

        updateLocalIntegrationSteps(steps, false, states);
        setUnsavedDataState();
    }
};

/**
 * Trigger click on step ID
 * @param {Number} stepId
 */
export const triggerClickOnStepId = (stepId) => {
    stepId && document && document?.querySelector(`.renderStepTypeItem-${stepId}`)?.click();
};

/**
 * Check whether the integration log output is minimized
 */
export const useIsIntegrationLogOutputMinimized = () =>
    useIntegrationBuilderValue('minimizeIntegrationLogOutput') === true;

/**
 * Restore integration log test output
 */
export const restoreIntegrationLogOutput = () => {
    setIntegrationBuilderStates({
        minimizeIntegrationLogOutput: false,
    });
};

export const useOriginalTriggerStepData = () =>
    useStoreValue('originalTriggerStepData', integrationBuilderStoreNamespace)(null);

export const getOriginalTriggerStepData = () =>
    getStoreState('originalTriggerStepData', integrationBuilderStoreNamespace)(null);

export const setOriginalTriggerStepData = (originalTriggerStepData) =>
    setStoreState('originalTriggerStepData')(_.cloneDeep(originalTriggerStepData));

/**
 * Opens the integration conditional step modal after adding a conditional step
 * @param {function|null} addConditionalInBetween
 * @param {function|null} addConditionalWithStepsInBranch
 */
export const openIntegrationConditionalStepModal = (
    addConditionalInBetween = null,
    addConditionalWithStepsInBranch = null,
) => {
    setIntegrationBuilderStates({
        integrationConditionalStepModalState: {
            isOpen: true,
            addConditionalInBetween,
            addConditionalWithStepsInBranch,
        },
    });
};

/**
 * Closes the integration conditional step modal
 */
export const closeIntegrationConditionalStepModal = () => {
    setIntegrationBuilderStates({
        integrationConditionalStepModalState: {
            isOpen: false,
            addConditionalInBetween: null,
            addConditionalWithStepsInBranch: null,
        },
    });
};
